import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import {fetchAuthSession} from 'aws-amplify/auth'
import {
  Account,
  AccountResponse,
  AutoPayParams,
  DeletePaymentAccountParam,
  BillingParam,
  Folio,
  FolioResponse,
  LeaseApplication,
  LeaseUnitDetailParam,
  PaymentAccount,
  PaymentParams,
  PaymentMaxParams,
  PaymentSessionParams,
  Ticket,
  TicketParams
} from '../types/app-types'
import {getConfig} from './config'
import {getLogger} from './logging'

const {residentPortalApiUrl} = getConfig()
const logger = getLogger('residentPortalApi')

export const residentPortalApi = createApi({
  reducerPath: 'residentPortalApi',
  baseQuery: fetchBaseQuery({
    baseUrl: residentPortalApiUrl,
    prepareHeaders: async (headers): Promise<Headers> => {
      try {
        // injects authorization token into request headers
        const session = await fetchAuthSession()
        if (session?.tokens?.idToken) {
          const token = session.tokens.idToken.toString()
          headers.set('Authorization', token)
        }
      } catch (e) {
        logger.logWarn('Auth token not set on HTTP request', {error: e})
      }
      return headers
    }
  }),
  tagTypes: [
    'Account',
    'BillingActivity',
    'BillingStatement',
    'Folio',
    'LeaseApplication',
    'LeaseUnitDetail',
    'PaymentAccount',
    'PaymentMethods',
    'PaymentSession',
    'PaymentMax',
    'Property',
    'Reservation',
    'Ticket'
  ],
  endpoints: builder => ({
    createLeaseApplication: builder.mutation({
      query(leaseApplication: LeaseApplication) {
        return {
          url: `/leaseApplication`,
          method: 'POST',
          body: leaseApplication
        }
      },
      invalidatesTags: ['LeaseApplication']
    }),
    createTicket: builder.mutation({
      query({
        reservationId,
        type,
        subject,
        description,
        unitId,
        priority,
        propertyId
      }: TicketParams) {
        return {
          url: `/reservations/${reservationId}/tickets`,
          method: 'POST',
          body: {
            type,
            subject,
            description,
            unitId,
            priority,
            propertyId
          }
        }
      },
      async onQueryStarted(
        {reservationId, type, subject}: TicketParams,
        {dispatch, queryFulfilled}
      ) {
        /*
          NOTE: manually insert created ticket into cache rather than trigger a refresh because there is a
          delay in HubSpot between when the ticket is created and when it is retrievable via their search API.
         */
        try {
          const createdTicket = await queryFulfilled
          dispatch(
            residentPortalApi.util.updateQueryData(
              'getTickets',
              reservationId,
              (tickets: Ticket[]) => {
                tickets.unshift(createdTicket.data as Ticket)
              }
            )
          )
        } catch (e) {
          logger.logError('Failed to create new ticket', {
            error: e
          })
        }
      }
    }),
    deletePaymentAccount: builder.mutation({
      query({
        accountReference,
        propertyId,
        reservationId
      }: DeletePaymentAccountParam) {
        return {
          url: `/paymentAccounts`,
          method: 'DELETE',
          body: {
            accountReference,
            propertyId,
            reservationId
          }
        }
      },
      invalidatesTags: ['PaymentAccount']
    }),
    getAccounts: builder.query({
      query() {
        return `/reservations`
      },
      providesTags: ['Account'],
      transformResponse(accountItems: AccountResponse[]): Account[] {
        return accountItems && accountItems.length
          ? accountItems.map(({properties}: AccountResponse) => {
              return {
                id: properties.hs_object_id,
                leaseSignatureId: properties.lease_signature_id,
                propertyCode: properties.pms_property_code,
                propertyName: properties.property_short_name,
                reservationId: properties.pms_reservation_id,
                roomNumber: properties.room_number,
                unitId: properties.unit,
                unitType: properties.unit_type,
                checkinDate: properties.checkin_date,
                checkoutDate: properties.checkout_date
              }
            })
          : []
      }
    }),
    getBillingActivity: builder.query({
      query({propertyId, reservationId}: BillingParam) {
        return `/billingActivity?propertyId=${propertyId}&reservationId=${reservationId}`
      },
      providesTags: ['BillingActivity']
    }),
    getBillingStatements: builder.query({
      query({propertyId, reservationId}: BillingParam) {
        return `/billingStatements?propertyId=${propertyId}&reservationId=${reservationId}`
      },
      providesTags: ['BillingStatement']
    }),
    getFolio: builder.query({
      query(reservationId: string) {
        return `/reservations/${reservationId}/folios`
      },
      providesTags: ['Folio'],
      transformResponse({folios}: FolioResponse): Folio {
        if (folios && folios.length) {
          for (const folio of folios) {
            if (folio.isMainFolio) {
              return folio
            }
          }
        }
        return null
      }
    }),
    getLeaseApplication: builder.query({
      query(leaseId: string) {
        return `/leaseApplication/${leaseId}`
      },
      providesTags: ['LeaseApplication']
    }),
    getLeaseUnitDetail: builder.query({
      query({propertyId, unitId}: LeaseUnitDetailParam) {
        return `/leaseUnitDetail?propertyId=${propertyId}&unitId=${unitId}`
      },
      providesTags: ['LeaseUnitDetail']
    }),
    getPaymentAccounts: builder.query({
      query(propertyId: string) {
        return `/paymentAccounts?propertyId=${propertyId}`
      },
      providesTags: ['PaymentAccount']
    }),
    getPaymentMethods: builder.query({
      query(propertyId: string) {
        return `/paymentMethods?propertyId=${propertyId}`
      },
      providesTags: ['PaymentMethods']
    }),
    getPaymentSession: builder.query({
      query({
        amountInCents,
        reservationId,
        propertyId,
        paymentAccount,
        paymentType,
        paymentOrigin,
        returnUrl,
        leaseApplicationId
      }: PaymentSessionParams) {
        const requestUrl = `/paymentSession`
        const bodyParams = {
          paymentAccount,
          paymentOrigin,
          propertyId,
          reservationId,
          returnUrl,
          paymentType,
          leaseApplicationId,
          amount: {
            value: amountInCents,
            currency: 'USD'
          },
          countryCode: 'US'
        }
        return {
          url: requestUrl,
          method: 'POST',
          body: bodyParams
        }
      },
      providesTags: (result, error, arg) => {
        const {
          amountInCents,
          reservationId,
          paymentAccount
        } = arg as PaymentSessionParams
        const serializedParams = `${reservationId}-${paymentAccount}-${amountInCents}`
        return result ? [{type: 'PaymentSession', id: serializedParams}] : []
      }
    }),
    getProperty: builder.query({
      query(propertyId: string) {
        return `/properties/${propertyId}`
      },
      providesTags: ['Property']
    }),
    getReservation: builder.query({
      query(reservationId: string) {
        return `/reservations/${reservationId}`
      },
      providesTags: ['Reservation']
    }),
    getPaymentMax: builder.query({
      query(params: PaymentMaxParams) {
        const {propertyId, reservationId} = params
        return `/paymentMax?propertyId=${propertyId}&reservationId=${reservationId}`
      },
      providesTags: ['PaymentMax']
    }),
    getTickets: builder.query({
      query(reservationId: string) {
        return `reservations/${reservationId}/tickets`
      },
      keepUnusedDataFor: 3600,
      providesTags: ['Ticket']
    }),
    listLeaseApplications: builder.query({
      query() {
        return `/leaseApplication`
      },
      providesTags: ['LeaseApplication']
    }),
    makePayment: builder.mutation({
      query({
        accountReference,
        amountInCents,
        propertyId,
        reservationId
      }: PaymentParams) {
        return {
          url: `/payment`,
          method: 'POST',
          body: {
            accountReference,
            amount: {
              value: amountInCents,
              currency: 'USD'
            },
            propertyId,
            reservationId
          }
        }
      },
      invalidatesTags: ['Folio']
    }),
    updateLeaseApplication: builder.mutation({
      query(leaseApplication: LeaseApplication) {
        return {
          url: `/leaseApplication/${leaseApplication.id}`,
          method: 'PUT',
          body: leaseApplication
        }
      }
      // invalidatesTags: ['LeaseApplication']
    }),
    updatePaymentAccountAutoPay: builder.mutation({
      query(params: AutoPayParams) {
        const {
          propertyId,
          reservationId,
          accountReference,
          autoPayEnabled,
          autoPayDayOfMonth,
          autoPayAmount
        } = params

        return {
          url: `/paymentAccounts`,
          method: 'PUT',
          body: {
            propertyId,
            reservationId,
            accountReference,
            autoPayEnabled,
            autoPayDayOfMonth,
            autoPayAmount
          }
        }
      },
      invalidatesTags: ['PaymentAccount']
    })
  })
})

export const {
  useCreateLeaseApplicationMutation,
  useCreateTicketMutation,
  useDeletePaymentAccountMutation,
  useGetAccountsQuery,
  useGetBillingActivityQuery,
  useGetBillingStatementsQuery,
  useGetFolioQuery,
  useGetLeaseApplicationQuery,
  useGetLeaseUnitDetailQuery,
  useGetPaymentAccountsQuery,
  useGetPaymentMethodsQuery,
  useGetPaymentSessionQuery,
  useGetPaymentMaxQuery,
  useGetPropertyQuery,
  useGetReservationQuery,
  useGetTicketsQuery,
  useListLeaseApplicationsQuery,
  useMakePaymentMutation,
  useUpdateLeaseApplicationMutation,
  useUpdatePaymentAccountAutoPayMutation
} = residentPortalApi
